import React, { useState, useEffect } from 'react';
import io from 'socket.io-client';
import { useLocation } from 'react-router-dom';
import { Settings2, MoonStar, Volume2, VolumeX, X } from 'lucide-react';
import './App.css';
import './Waves.css';
// import './Dots.css';

// Improvimania Imports
import titleImage from './image/improvimania/title.png';
import Players from './apps/Players';
import Hosts from './apps/Hosts';
import titleTheme from './sound/improvimania/theme.m4a';
import speakingTheme from './sound/improvimania/speaking.m4a';
import guessingTheme from './sound/improvimania/guessing.m4a';
import finishTheme from './sound/improvimania/finish.m4a';
import guessingTitle from './sound/guessing/guesstitle.m4a';

// Menu Imports
import AudioPlayer from './apps/AudioPlayer';
import Quit from './apps/Quit';
import Game from './apps/Game';
import AnimatedTitle from './apps/AnimatedTitle';

// Import the new ImprovGame module
import ImprovGame from './apps/improvimania/Improv';

const STORAGE_KEYS = {
    clientId: 'partypackClientId',
    reconnect: 'partypackReconnectState',
    theme: 'partypackTheme',
    musicEnabled: 'partypackMusicEnabled'
};

const getOrCreateClientId = () => {
    const existing = localStorage.getItem(STORAGE_KEYS.clientId);
    if (existing) return existing;

    const generated = (window.crypto && typeof window.crypto.randomUUID === 'function')
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    localStorage.setItem(STORAGE_KEYS.clientId, generated);
    return generated;
};

const readReconnectState = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEYS.reconnect);
        return raw ? JSON.parse(raw) : null;
    } catch (err) {
        console.warn('Failed to parse reconnect state:', err);
        return null;
    }
};

const writeReconnectState = (state) => {
    localStorage.setItem(STORAGE_KEYS.reconnect, JSON.stringify(state));
};

const clearReconnectState = () => {
    localStorage.removeItem(STORAGE_KEYS.reconnect);
};

const getInitialTheme = () => {
        const savedTheme = localStorage.getItem(STORAGE_KEYS.theme);
        if (savedTheme === 'light' || savedTheme === 'dark') {
                return savedTheme;
        }

    if (typeof window !== 'undefined' && window.matchMedia) {
      if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark';
      }
    }
    return 'light'; // Default to light theme
  };

const getInitialMusicEnabled = () => {
        const savedValue = localStorage.getItem(STORAGE_KEYS.musicEnabled);
        if (savedValue === 'false') {
                return false;
        }

        return true;
};

const GAME_SYNC_EVENTS = {
    'guessing': 'guessingSyncState',
    'guessing-gameshow': 'guessingSyncState',
    'guessing-spiral': 'guessingSyncState',
    'andscene': 'andSceneSyncState',
    'wordmonster': 'wordMonsterSyncState',
    'reaction': 'reactionSyncState'
};

const getBodyClassName = (nextTheme, activeGame) => {
    const classes = [nextTheme];

    if (activeGame) {
        const gameClassSuffix = String(activeGame)
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');

        if (gameClassSuffix) {
            classes.push(`game-${gameClassSuffix}`);
        }
    }

    return classes.join(' ');
};
  
function App() {
    const clientId = getOrCreateClientId();
    const [ipAddress, setIpAddress] = useState(sessionStorage.getItem('ipAddress'));
    const [serverIP, setServerIP] = useState('');
    const [role, setRole] = useState(null);
    const [guessesMade, setGuessesMade] = useState([]);
    const [socket, setSocket] = useState(null);
    const [sessionId, setSessionId] = useState('');
    const [playerName, setPlayerName] = useState('');
    const [players, setPlayers] = useState([]);
    const [rounds, setRounds] = useState(1);
    const [gameStarted, setGameStarted] = useState(false);
    const [currentLine, setCurrentLine] = useState(null);
    const [sentGuess, setSentGuess] = useState(false);
    const [currentRound, setCurrentRound] = useState(1);
    const [playerRole, setPlayerRole] = useState(null);
    const [leaderboard, setLeaderboard] = useState({});
    const [joinedSession, setJoinedSession] = useState(false);
    const [isEndScene, setIsEndScene] = useState(false);
    const [isEndGame, setIsEndGame] = useState(false);
    const [serverType, setServerType] = useState('fayaz.fly.dev');
    const [gameMode, setGameMode] = useState('freeforall');
    const [isSpeaker, setIsSpeaker] = useState(false);
    const [connectionError, setConnectionError] = useState(false); 
    const [connectionWaiting, setConnectionWaiting] = useState(false);
    const [kicked, setKicked] = useState(false);
    const [theme, setTheme] = useState(getInitialTheme);
    const [musicEnabled, setMusicEnabled] = useState(getInitialMusicEnabled);
    const [forceRemove, setForceRemove] = useState(false);
    const [confirmQuit, setConfirmQuit] = useState(false);
    const [joinError, setJoinError] = useState('');
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [game, setGame] = useState(sessionStorage.getItem('game'));
    const [clientVersion] = useState('1.0.2');
    const [serverVersion, setServerV] = useState('Disconnected');
    const [availableImprovScripts, setAvailableImprovScripts] = useState([]);
    const [selectedImprovScript, setSelectedImprovScript] = useState('everyday');
    const [hostConnected, setHostConnected] = useState(true);
    const isCustomServer = serverType === 'custom';

    let [sessionList, setSessionList] = useState([]);


    const [sessionCreated, setSessionCreated] = useState(() => {
        const storedValue = sessionStorage.getItem('sessionCreated');
        return storedValue === 'true' ? true : false;
      });

    const letterToNumber = (letter) => {
        if (letter === 'X') return '.';
        return (letter.charCodeAt(0) - 65).toString();
    };

    const lettersToIp = (letters) => {
        if (letters === 'localhost') return 'localhost';
        return letters.split('')
                    .map(letter => letterToNumber(letter))
                    .join('');
    };

    const persistHostReconnectState = (nextSessionId, hostToken, nextGame) => {
        const payload = {
            role: 'host',
            sessionId: nextSessionId,
            hostToken: hostToken || null,
            game: nextGame || null,
            updatedAt: Date.now()
        };
        writeReconnectState(payload);
    };

    const persistPlayerReconnectState = (nextSessionId, nextPlayerName) => {
        const payload = {
            role: 'player',
            sessionId: nextSessionId,
            playerName: nextPlayerName,
            updatedAt: Date.now()
        };
        writeReconnectState(payload);
    };

    const attemptStoredReconnect = (targetSocket) => {
        const reconnectState = readReconnectState();
        if (!reconnectState || !reconnectState.role || !reconnectState.sessionId) return;

        if (reconnectState.role === 'host') {
            setRole('host');
            setSessionId(reconnectState.sessionId);
            setSessionCreated(true);
            targetSocket.emit('reconnectHost', {
                sessionId: reconnectState.sessionId,
                hostToken: reconnectState.hostToken,
                allowTakeover: false
            });
            return;
        }

        if (reconnectState.role === 'player' && reconnectState.playerName) {
            setRole('player');
            setPlayerName(reconnectState.playerName);
            targetSocket.emit('reconnectPlayer', {
                sessionId: reconnectState.sessionId,
                playerName: reconnectState.playerName
            });
        }
    };
    
    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const customIpAddress = searchParams.get('ipAddress');

        if (customIpAddress) {
            sessionStorage.setItem('ipAddress', customIpAddress);
            setIpAddress(customIpAddress);
            console.log('Session storage set: ipAddress =', customIpAddress);

            const newUrl = window.location.protocol + "//" + window.location.host + window.location.pathname;
            window.history.replaceState({ path: newUrl }, '', newUrl);
        }
    }, []);

    useEffect(() => {
        document.body.className = getBodyClassName(theme, game);
        localStorage.setItem(STORAGE_KEYS.theme, theme);
    }, [theme, game]);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEYS.musicEnabled, String(musicEnabled));
    }, [musicEnabled]);

    useEffect(() => {
        if (!settingsOpen) return undefined;

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setSettingsOpen(false);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [settingsOpen]);

    useEffect(() => {
        if (!socket) return undefined;

        const handleConnect = () => {
            setConnectionError(false);
            attemptStoredReconnect(socket);
        };

        const handleSessionCreated = ({ sessionId: createdSessionId, hostToken }) => {
            const shortSessionId = String(createdSessionId || '').toUpperCase();
            setSessionId(shortSessionId);
            setSessionCreated(true);
            setHostConnected(true);
            sessionStorage.setItem('sessionId', shortSessionId);
            sessionStorage.setItem('sessionCreated', 'true');
            persistHostReconnectState(shortSessionId, hostToken, game);
        };

        const handleUpdatePlayers = ({ players: nextPlayers }) => {
            setPlayers(nextPlayers);
            console.log('Players updated!');
        };

        const handlePlayerRemoved = ({ removedPlayer, kickPlayer }) => {
            if (socket.id === removedPlayer) {
                resetEverything();
                setKicked(kickPlayer);
            }
        };

        const handleSetGame = ({ game: nextGame }) => {
            setGame(nextGame);
            if (nextGame) {
                sessionStorage.setItem('game', nextGame);
                const reconnectState = readReconnectState();
                if (reconnectState?.role === 'host') {
                    persistHostReconnectState(reconnectState.sessionId, reconnectState.hostToken, nextGame);
                }
            }
        };

        const handleHostReconnectFailed = () => {
            clearHostSessionState();
            clearReconnectState();
        };

        const handleAvailableSessions = (sessions) => {
            setSessionList(sessions);
        };

        const handleLegacyReconnect = ({ name, sessionId: reconnectedSessionId, players: sessionPlayers }) => {
            setRole('player');
            setPlayerName(name);
            setPlayers(sessionPlayers || []);
            setSessionId(reconnectedSessionId);
            setJoinedSession(true);
            setHostConnected(true);
            persistPlayerReconnectState(reconnectedSessionId, name);
        };

        const handlePlayerReconnected = ({ sessionId: reconnectedSessionId, name, game: reconnectGame, players: sessionPlayers }) => {
            setRole('player');
            setPlayerName(name);
            setPlayers(sessionPlayers || []);
            setSessionId(reconnectedSessionId);
            setJoinedSession(true);
            setHostConnected(true);
            setJoinError('');
            if (reconnectGame) setGame(reconnectGame);
            persistPlayerReconnectState(reconnectedSessionId, name);
            sessionStorage.setItem('role', 'player');
        };

        const handlePlayerJoined = ({ sessionId: joinedSessionId, name, game: joinedGame, players: sessionPlayers }) => {
            setRole('player');
            setPlayerName(name);
            setPlayers(sessionPlayers || []);
            setSessionId(joinedSessionId);
            setJoinedSession(true);
            setHostConnected(true);
            setJoinError('');
            if (joinedGame) setGame(joinedGame);
            sessionStorage.setItem('role', 'player');
            persistPlayerReconnectState(joinedSessionId, name);
        };

        const handleHostReconnected = ({ sessionId: reconnectedSessionId, game: reconnectGame, players: sessionPlayers, hostToken }) => {
            setRole('host');
            setSessionId(reconnectedSessionId);
            setSessionCreated(true);
            setPlayers(sessionPlayers || []);
            setHostConnected(true);
            if (reconnectGame) setGame(reconnectGame);
            sessionStorage.setItem('sessionId', reconnectedSessionId);
            sessionStorage.setItem('sessionCreated', 'true');
            sessionStorage.setItem('role', 'host');
            persistHostReconnectState(reconnectedSessionId, hostToken, reconnectGame || game);
        };

        const handlePlayerReconnectFailed = () => {
            clearReconnectState();
            setJoinedSession(false);
            setGame(null);
            setSessionId('');
        };

        const handlePlayerJoinFailed = ({ reason, sessionId: failedSessionId }) => {
            setJoinedSession(false);
            setSessionId('');
            setGame(null);

            if (reason === 'name-taken') {
                setJoinError('That name is already claimed in this session. Choose a different name.');
            } else if (reason === 'invalid-name') {
                setJoinError('Enter a valid name before joining.');
            } else if (reason === 'session-not-found') {
                setJoinError(`Session ${failedSessionId || ''} was not found.`.trim());
            } else {
                setJoinError('Unable to join session.');
            }
        };

        const handleHostStatus = ({ connected }) => {
            setHostConnected(Boolean(connected));
        };

        const handleSessionClosed = () => {
            clearReconnectState();
            resetEverything();
        };

        socket.on('connect', handleConnect);
        socket.on('sessionCreated', handleSessionCreated);
        socket.on('updatePlayers', handleUpdatePlayers);
        socket.on('playerRemoved', handlePlayerRemoved);
        socket.on('setGame', handleSetGame);
        socket.on('hostReconnectFailed', handleHostReconnectFailed);
        socket.on('availableSessions', handleAvailableSessions);
        socket.on('reconnect', handleLegacyReconnect);
        socket.on('playerReconnected', handlePlayerReconnected);
        socket.on('playerJoined', handlePlayerJoined);
        socket.on('hostReconnected', handleHostReconnected);
        socket.on('playerReconnectFailed', handlePlayerReconnectFailed);
        socket.on('playerJoinFailed', handlePlayerJoinFailed);
        socket.on('sessionHostStatus', handleHostStatus);
        socket.on('sessionClosed', handleSessionClosed);

        return () => {
            socket.off('connect', handleConnect);
            socket.off('sessionCreated', handleSessionCreated);
            socket.off('updatePlayers', handleUpdatePlayers);
            socket.off('playerRemoved', handlePlayerRemoved);
            socket.off('setGame', handleSetGame);
            socket.off('hostReconnectFailed', handleHostReconnectFailed);
            socket.off('availableSessions', handleAvailableSessions);
            socket.off('reconnect', handleLegacyReconnect);
            socket.off('playerReconnected', handlePlayerReconnected);
            socket.off('playerJoined', handlePlayerJoined);
            socket.off('hostReconnected', handleHostReconnected);
            socket.off('playerReconnectFailed', handlePlayerReconnectFailed);
            socket.off('playerJoinFailed', handlePlayerJoinFailed);
            socket.off('sessionHostStatus', handleHostStatus);
            socket.off('sessionClosed', handleSessionClosed);
        };
    }, [socket, game]);

    useEffect(() => {
        if (!socket || !sessionId || !game) return;

        const syncEvent = GAME_SYNC_EVENTS[game];
        if (!syncEvent) return;

        socket.emit(syncEvent, { sessionId });
    }, [socket, sessionId, game, role, players.length]);

    const toggleTheme = () => {
        setTheme(prevTheme => {
          const newTheme = prevTheme === 'light' ? 'dark' : 'light';

          // Update title bar if it exists
          const titleBar = document.querySelector('.title-bar');
          if (titleBar) {
            titleBar.classList.remove('light', 'dark');
            titleBar.classList.add(newTheme);
          }
          
          return newTheme;
        });
      };

        const toggleMusic = () => {
                setMusicEnabled((prev) => !prev);
        };

    const resetEverything = () => {
        if (socket) {
            socket.disconnect();
        }

        setRole(null);
        setIpAddress('');
        setPlayerName('');
        setJoinedSession(false);
        setRounds(0);
        setPlayers([]);
        setSessionId('');
        setSocket(null);
        setSessionList([]);
        setGame(null);
        setSessionCreated(false);
        setHostConnected(true);
        setJoinError('');
        setSettingsOpen(false);
        
        sessionStorage.clear();
        clearReconnectState();
    };

    const convertIP = () => {
        if (!ipAddress) return null;

        const rawInput = String(ipAddress).trim();
        if (!rawInput) return null;

        const normalizedInput = rawInput.toLowerCase();
        if (normalizedInput === 'localhost') {
            return 'localhost';
        }

        // If input has a period, assume direct hostname/IP value.
        if (rawInput.includes('.')) {
            return rawInput;
        }

        // Otherwise treat all-letter value as short code and decode to LAN IP.
        if (/^[a-z]+$/i.test(rawInput)) {
            const numericIp = lettersToIp(rawInput.toUpperCase());
            console.log('Decoded short IP:', numericIp);

            const octets = numericIp.split('.');

            if (octets.length === 1) {
                return `192.168.1.${octets[0]}`;
            }

            if (octets.length === 2) {
                return `192.168.${octets[0]}.${octets[1]}`;
            }

            if (octets.length === 3) {
                return `192.${octets[0]}.${octets[1]}.${octets[2]}`;
            }

            if (octets.length === 4) {
                return `${octets[0]}.${octets[1]}.${octets[2]}.${octets[3]}`;
            }

            return numericIp;
        }

        return null;
    };

    const buildServerUrl = (target) => {
        if (!target) return null;

        if (/^https?:\/\//i.test(target)) {
            return target;
        }

        const isLocalTarget = /^(localhost|127\.|10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/i.test(target);
        const hasPort = /:\d+$/.test(target);
        const hostWithPort = hasPort ? target : `${target}:443`;
        const protocol = isLocalTarget ? 'http' : 'https';

        return `${protocol}://${hostWithPort}`;
    };

    // Client-side connection logic
    const connectToServer = ({ targetServerType, targetIpAddress, queryRole, forceReconnect = false } = {}) => {
        if (socket && !forceReconnect) {
            return;
        }

        const resolvedServerType = targetServerType || serverType;
        const resolvedIpAddress = typeof targetIpAddress !== 'undefined' ? targetIpAddress : ipAddress;
        const resolvedIP = resolvedServerType === 'custom' ? (() => {
            if (!resolvedIpAddress) return null;
            const rawInput = String(resolvedIpAddress).trim();
            if (!rawInput) return null;
            const normalizedInput = rawInput.toLowerCase();
            if (normalizedInput === 'localhost') return 'localhost';
            if (rawInput.includes('.')) return rawInput;
            if (/^[a-z]+$/i.test(rawInput)) {
                const numericIp = lettersToIp(rawInput.toUpperCase());
                const octets = numericIp.split('.');
                if (octets.length === 1) return `192.168.1.${octets[0]}`;
                if (octets.length === 2) return `192.168.${octets[0]}.${octets[1]}`;
                if (octets.length === 3) return `192.${octets[0]}.${octets[1]}.${octets[2]}`;
                if (octets.length === 4) return `${octets[0]}.${octets[1]}.${octets[2]}.${octets[3]}`;
                return numericIp;
            }
            return null;
        })() : resolvedServerType;

        if (!resolvedIP) {
            console.error("Invalid IP");
            return;
        }

        const url = buildServerUrl(resolvedIP);

        if (!url) {
            console.error('Invalid server URL');
            return;
        }

        console.log("Sending WebSocket request to", url)
        
        const newSocket = io(url, {
            transports: ['websocket'],
            query: {
                role: typeof queryRole !== 'undefined' ? queryRole : sessionStorage.getItem('role'),
                clientId
            }
        });

        setConnectionError(false);
        setConnectionWaiting(true);

        // Handle connection error
        newSocket.on('connect_error', (error) => {
            console.error('Connection error:', error);
            setConnectionError(true);
            setConnectionWaiting(false);
            newSocket.close()
        });

        newSocket.on('connect', data => {
            setSocket(newSocket);
            setConnectionWaiting(false);
            const storedTarget = resolvedServerType === 'custom' ? resolvedIpAddress : resolvedServerType;
            if (storedTarget) {
                sessionStorage.setItem('ipAddress', storedTarget);
            }
        });

        newSocket.on('clientIdentity', ({ clientId: serverClientId }) => {
            if (serverClientId) {
                localStorage.setItem(STORAGE_KEYS.clientId, serverClientId);
            }
        });

        newSocket.on('serverVersion', (version) => {
          setServerV(version);
      });
      newSocket.on('serverIpAddress', (serverIpAddress) => {
        setServerIP(serverIpAddress);
    });
    };

    const switchServer = (nextServerType, nextIpAddress = '') => {
        if (socket) {
            socket.disconnect();
        }

        resetEverything();
        setServerType(nextServerType);
        setIpAddress(nextIpAddress);
        connectToServer({
            targetServerType: nextServerType,
            targetIpAddress: nextIpAddress,
            queryRole: null,
            forceReconnect: true,
        });
    };

        useEffect(() => {
                if (socket) return;

                const reconnectState = readReconnectState();
                if (!reconnectState || !reconnectState.role) return;

                setRole(reconnectState.role);
                connectToServer();
        }, [socket]);

    const createSession = ( game ) => {
        if (socket) {
            socket.emit('createSession', game);
            sessionStorage.setItem('sessionCreated', 'true');
            sessionStorage.setItem('role', role);
            setHostConnected(true);
        }
    };

    const clearHostSessionState = () => {
        setSessionCreated(false);
        setSessionId('');
        setGame(null);
        setPlayers([]);
        sessionStorage.removeItem('sessionCreated');
        sessionStorage.removeItem('sessionId');
        sessionStorage.removeItem('game');
        clearReconnectState();
    };
    
    const joinSession = (clickedSessionId) => {
        console.log('Attempting to join session:', clickedSessionId);
        setJoinError('');
        
        if (!socket) {
            console.error('Socket is not initialized');
            return;
        }
        
        if (!playerName) {
            console.error("No player name when trying to join!");
            return;
        }
        
        // Update the sessionId state
        const normalizedSessionId = String(clickedSessionId).toUpperCase();
        setSessionId(normalizedSessionId);
        
        // Emit the join event
        console.log('Emitting joinSession event:', { sessionId: normalizedSessionId, playerName });
        socket.emit('joinSession', { sessionId: normalizedSessionId, playerName });
        
        console.log('Join session process completed');
    };

    const reconnectHostSession = (targetSessionId, allowTakeover = true) => {
        if (!socket) return;

        const normalizedSessionId = String(targetSessionId || '').toUpperCase();
        if (!normalizedSessionId) return;

        const reconnectState = readReconnectState();
        const savedHostToken = reconnectState?.role === 'host' && reconnectState.sessionId === normalizedSessionId
            ? reconnectState.hostToken
            : null;

        setRole('host');
        setSessionId(normalizedSessionId);
        setSessionCreated(true);
        sessionStorage.setItem('role', 'host');
        sessionStorage.setItem('sessionId', normalizedSessionId);
        sessionStorage.setItem('sessionCreated', 'true');

        socket.emit('reconnectHost', {
            sessionId: normalizedSessionId,
            hostToken: savedHostToken,
            allowTakeover
        });
    };

    const {
        nextLine,
        guessAdlibber,
        voteAdlibLine,
        activeVote,
        voteBudgetRemaining,
        votedLineIds,
        lineVoteFeed
    } = ImprovGame({
        socket,
        sessionId,
        setGameStarted,
        setRounds,
        setCurrentRound,
        setPlayerRole,
        setLeaderboard,
        setIsEndScene,
        setIsSpeaker,
        setCurrentLine,
        setSentGuess,
        setIsEndGame,
        role,
        players,
        leaderboard,
        setGuessesMade,
        setGameMode,
        setAvailableImprovScripts,
        setSelectedImprovScript
    });

    const removePlayer = (playerToRemove, forceRemove) => {
        console.log(`Asked the server to remove ${playerToRemove}`)
        socket.emit('removePlayer', { sessionId, playerToRemove, forceRemove });
      };

    const returnToPartyPack = () => {
        clearHostSessionState();
        setPlayers([]);
    };

    const renderGame = () => (
      <Game
        isEndGame={isEndGame}
        joinedSession={joinedSession}
        sessionId={sessionId}
        setSessionId={setSessionId}
        playerName={playerName}
        setPlayerName={setPlayerName}
        joinSession={joinSession}
        gameStarted={gameStarted}
        setGameStarted={setGameStarted}
        players={players}
        setPlayers={setPlayers}
        playerRole={playerRole}
        isEndScene={isEndScene}
        currentLine={currentLine}
        isSpeaker={isSpeaker}
        nextLine={nextLine}
        guessAdlibber={guessAdlibber}
        voteAdlibLine={voteAdlibLine}
        sessionList={sessionList}
        leaderboard={leaderboard}
        kicked={kicked}
        titleTheme={titleTheme}
        AudioPlayer={AudioPlayer}
        speakingTheme={speakingTheme}
        guessingTheme={guessingTheme}
        sentGuess={sentGuess}
        game={game}
        socket={socket}
        serverIP={serverIP}
        sessionCreated={sessionCreated}
        createSession={createSession}
        rounds={rounds}
        setRounds={setRounds}
        currentRound={currentRound}
        removePlayer={removePlayer}
        gameMode={gameMode}
        setGameMode={setGameMode}
        availableImprovScripts={availableImprovScripts}
        selectedImprovScript={selectedImprovScript}
        setSelectedImprovScript={setSelectedImprovScript}
        setForceRemove={setForceRemove}
        forceRemove={forceRemove}
        setGame={setGame}
        theme={theme}
        role={role}
        guessesMade={guessesMade}
        activeVote={activeVote}
        voteBudgetRemaining={voteBudgetRemaining}
        votedLineIds={votedLineIds}
        lineVoteFeed={lineVoteFeed}
        setLeaderboard={setLeaderboard}
                joinError={joinError}
                isMusicMuted={!musicEnabled}
        returnToPartyPack={returnToPartyPack}
        reconnectHostSession={reconnectHostSession}
        hostConnected={hostConnected}
                serverType={serverType}
                setServerType={setServerType}
                ipAddress={ipAddress}
                setIpAddress={setIpAddress}
                connectToServer={connectToServer}
                switchServer={switchServer}
        resetEverything={resetEverything}
      />
    );

  return (
    <div>
    {/* Audio components */}
    <AudioPlayer audioSrc={guessingTheme} loopStart={0} loopEnd={16} isPlaying={ game === 'improv' && isEndScene} isMuted={!musicEnabled}/>
    <AudioPlayer audioSrc={speakingTheme} loopStart={0} loopEnd={12} isPlaying={ game === 'improv' && gameStarted && !isEndScene} isMuted={!musicEnabled}/>
    <AudioPlayer audioSrc={titleTheme} loopStart={24} loopEnd={71.9} isPlaying={(game === 'improv' || game === null) && !gameStarted} isMuted={!musicEnabled} />
    {/* <AudioPlayer audioSrc={guessingTitle} loopStart={13} loopEnd={48} isPlaying={(game === 'guessing' || game === 'trivia') && !gameStarted}  /> */}
    <AudioPlayer audioSrc={finishTheme} loop={false} isPlaying={isEndGame} isMuted={!musicEnabled} />
    

    {/* Main content */}
    <div className="main-content">
        {!socket ? (
            <div className="App landing-screen">
            <AnimatedTitle title="play.fayaz.One" />

            <div className="landing-row">
                
                {/* Buttons */}
                {!isCustomServer && !connectionWaiting && (
                <div className="landing-buttons">
                    <button
                    onClick={() => {
                        clearHostSessionState();
                        setRole('host');
                        connectToServer();
                        sessionStorage.setItem('role', 'host');
                    }}
                    >
                    Host a Game
                    </button>

                    <button
                    onClick={() => {
                        setRole('player');
                        connectToServer();
                        sessionStorage.setItem('role', 'player');
                    }}
                    >
                    Join as Player
                    </button>
                </div>
                )}

                {/* Server Selection */}
                <div className="server-controls">
                <div className="server-controls-label">Server</div>
                <select
                    className="server-select"
                    value={serverType}
                    onChange={(e) => setServerType(e.target.value)}
                >
                    <option value="fayaz.fly.dev">Production</option>
                    <option value="localhost">Dev</option>
                    <option value="custom">Local</option>
                </select>

                {isCustomServer && (
                    <>
                    <input
                        className="server-input"
                        type="text"
                        value={ipAddress}
                        placeholder="Room code / IP"
                        onChange={(e) => setIpAddress(e.target.value)}
                    />
                    <button onClick={connectToServer}>Connect</button>
                    </>
                )}
                </div>

            </div>

            {/* Status messages stay below */}
            {kicked && <h2 style={{ color: 'red' }}>You have been kicked by the host.</h2>}
            {connectionWaiting && <h2>Attempting connection, please wait.</h2>}
            {!(serverType === 'localhost') && connectionError && (
                <p style={{ color: 'red' }}>
                Connection failed. Please check the IP address and try again.
                </p>
            )}
            {serverType === 'localhost' && connectionError && (
                <p style={{ color: 'red' }}>
                Connection failed. Is the local development server running?
                </p>
            )}
            </div>
        ) : !role ? (
            <div className='App role-selection'>
                <h1>Choose a Role</h1>
                <button onClick={() => {clearHostSessionState(); setRole('host'); sessionStorage.setItem('role', 'host')}}>Host</button>
                <button onClick={() => {setRole('player'); sessionStorage.setItem('role', 'player')}}>Player</button>
            </div>
        ) : (
            renderGame()
        )}
            {role === 'player' && sessionId && !hostConnected && (
                <div className="host-reconnect-overlay" role="alert" aria-live="assertive">
                    <div className="host-reconnect-card">
                        <h2>Waiting For Host To Rejoin</h2>
                        <p>The game is paused while we reconnect the host.</p>
                        <p>If the host does not return, someone can take over from Host mode.</p>
                    </div>
                </div>
            )}
            <button
                className="settings-button"
                onClick={() => setSettingsOpen(true)}
                aria-label="Open settings"
                type="button"
            >
                <Settings2 size={22} />
            </button>
            {settingsOpen && (
                <div className="settings-backdrop" onClick={() => setSettingsOpen(false)}>
                    <div className="settings-panel" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true" aria-label="Settings">
                        <div className="settings-header">
                            <div>
                                <div className="settings-title">Settings</div>
                                <div className="settings-subtitle">Theme and audio preferences</div>
                            </div>
                            <button className="settings-close" type="button" onClick={() => setSettingsOpen(false)} aria-label="Close settings">
                                <X size={16} />
                            </button>
                        </div>

                        <div className="settings-row">
                            <div>
                                <div className="settings-row-label">Theme</div>
                                <div className="settings-row-help">Switch between light and dark presentation.</div>
                            </div>
                            <button className="settings-action" type="button" onClick={toggleTheme}>
                                <MoonStar size={16} />
                                {theme === 'light' ? 'Light' : 'Dark'}
                            </button>
                        </div>

                        <div className="settings-row">
                            <div>
                                <div className="settings-row-label">Music</div>
                                <div className="settings-row-help">Pause and resume background music from its current position.</div>
                            </div>
                            <button className="settings-action" type="button" onClick={toggleMusic}>
                                {musicEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
                                {musicEnabled ? 'On' : 'Off'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {role && sessionId && (
            <button 
                className="back-to-title-button" 
                onClick={() => {
                    // If in game selection (no game selected), just disconnect silently
                    if (!game) {
                        resetEverything();
                        return;
                    }
                    // Otherwise show confirmation popup
                    setConfirmQuit(true);
                }}
            >
                {theme === 'light' ? 'Return' : 'Leave'}
            </button>
            )}

            {confirmQuit && (
                <Quit 
                    playerName={playerName} 
                    forceRemove={forceRemove} 
                    removePlayer={removePlayer} 
                    resetEverything={resetEverything}
                    setConfirmQuit={setConfirmQuit}
                    setGame={setGame}
                    role={role}
                    socket={socket}
                    sessionId={sessionId}
                    inGameContext={true}
                />
            )}
        {serverType === 'localhost' && (
            <div className="version-text smalltext">
                Client Version: {clientVersion}
                <br />
                Server Version: {serverVersion}
            </div>
        )}
        </div>
        </div>
    );
}

export default App;
